import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';

/** 评价类型（`sv_satisfaction.eval_type`）。 */
export type SatisfactionEvalType = 'DELIVERY' | 'MATERIAL' | 'AFTER_SALE' | 'PLATFORM';

export interface SatisfactionRow {
  id: Id;
  orgId: Id;
  deptId: Id;
  deptName: string | null;
  evalType: string;
  bizType: string | null;
  bizNo: string | null;
  supplierId: Id;
  supplierName: string | null;
  /** 1–5 分。 */
  scoreTotal: number;
  scoreDetailJson: string | null;
  tags: string | null;
  content: string | null;
  picIds: string | null;
  evalUserId: Id;
  evalUserName: string | null;
  evalAt: string;
  anonFlag: boolean;
  replyUserId: Id;
  replyContent: string | null;
  replyAt: string | null;
  /** `SUBMITTED` / `REPLIED` / `CLOSED`。 */
  status: string;
}

export interface SatisfactionListQuery {
  evalType?: string;
  status?: string;
  supplierId?: Id;
  /** 业务类型（精确匹配，如 `DELIVERY`）。 */
  bizType?: string;
  /** 业务单号（精确匹配，如配送单号）。 */
  bizNo?: string;
  lastId?: Id;
  size?: number;
}

export interface SatisfactionListResult {
  items: SatisfactionRow[];
  hasMore: boolean;
  nextLastId: Id;
  /** 当前页汇总：条数 / 平均分 / 好评率（≥4 分占比）。 */
  summary: { count: number; avgScore: number; goodRate: number };
}

export interface SatisfactionCreateRequest {
  evalType?: SatisfactionEvalType;
  deptId?: Id;
  deptName?: string;
  supplierId?: Id;
  supplierName?: string;
  bizType?: string;
  bizNo?: string;
  /** 1–5。 */
  scoreTotal: number;
  /** 分项 JSON，如 `{"时效":5,"质量":4,"态度":5,"完整性":4}`。 */
  scoreDetailJson?: string;
  tags?: string;
  content?: string;
  picIds?: string;
  anonFlag?: boolean;
}

export interface SatisfactionCreateResult {
  id: Id;
  status: string;
  evalAt: string;
  /** 该业务单据已有评价，本次未新增（返回原评价，服务端幂等）。 */
  duplicated?: boolean;
}

export interface SatisfactionReplyResult {
  id: Id;
  status: string;
  replyAt: string;
  /** 配套会话线程号（`SV_SATISFACTION-{id}`）。 */
  threadNo: string;
}

export interface SatisfactionStats {
  count: number;
  avgScore: number;
  /** ≥4 分占比 0~1。 */
  goodRate: number;
  replyRate: number;
}

/** 满意度评价（配送/质量/售后/平台四类，客服可回复；docs/03 §K1）。 */
export function createSatisfactionApi(http: HttpClient) {
  return {
    list(query: SatisfactionListQuery = {}): Promise<SatisfactionListResult> {
      return http.request<SatisfactionListResult>({ path: '/wl/satisfaction', query: toQuery(query) });
    },
    stats(): Promise<SatisfactionStats> {
      return http.request<SatisfactionStats>({ path: '/wl/satisfaction/stats' });
    },
    create(body: SatisfactionCreateRequest): Promise<SatisfactionCreateResult> {
      return http.request<SatisfactionCreateResult>({ path: '/wl/satisfaction', method: 'POST', body });
    },
    reply(id: Id, replyContent: string): Promise<SatisfactionReplyResult> {
      return http.request<SatisfactionReplyResult>({
        path: `/wl/satisfaction/${id}/reply`,
        method: 'POST',
        body: { replyContent }
      });
    }
  };
}

export type SatisfactionApi = ReturnType<typeof createSatisfactionApi>;
